<script setup>
import BuilderPricing from "./components/builder/Pricing.vue";
import AppSection from "./components/app/Section.vue";
import BuilderSkeltonLoader from "./components/builder/SkeltonLoader.vue";
import BuilderColourPicker from "./components/builder/ColourPicker.vue";
import BuilderSizeSelector from "./components/builder/SizeSelector.vue";
import BuilderPersonalisation from "./components/builder/Personalisation.vue";
import BuilderAddToBasket from "./components/builder/AddToBasket.vue";
import { useHandleBasket } from "./composeables/useHandleBasket";
import { useHandleAddToCart } from "./composeables/useHandleAddToCart";
import { IonIcon } from "@ionic/vue";
import { refresh } from "ionicons/icons";
import { ref, onMounted, watch, computed } from "vue";
import { useCookies } from "@vueuse/integrations/useCookies";

const props = defineProps({
	id: Number,
});

const cookies = useCookies(["woocommerce_show_tax"]);
const taxStatus = ref(cookies.get("woocommerce_show_tax") ?? false);

const taxStatusBoolean = computed(() => {
	return typeof taxStatus.value !== "boolean" ? false : taxStatus.value;
});

const pending = ref(true);

const productData = ref({
	pricing: [],
	colours: [],
	sizes: [],
	personalisation: false,
	size_guide: "",
	previous_logo: [],
	comments_only: false,
	non_taxable: false,
	ctas_description: "",
	phone_cta: "",
	phone_number: 0,
	form_cta: "",
	form_shortcode: "",
});

const { basket, calculateTotal } = useHandleBasket(
	productData,
	taxStatusBoolean
);
const { addToBasket, isAddingToBasket, successNotification } =
	useHandleAddToCart(productData, basket, props.id);

const handleRemovePersonalisationItem = (index) => {
	basket.value.personalisation.splice(index, 1);
};

const handleAddPersonalisationItem = () => {
	basket.value.personalisation.push({
		logo: null,
		location: "",
		type: "",
		numberOfColours: "",
		large: false, // Based on global types
		setup: 0.0,
		price_type: "", // Based on global types
		error: "",
		price_per_item: 0.0,
	});
};

const sizeGuideLink = computed(() => {
	if (!productData.value.size_guide) return;
	return {
		text: "Size Guide",
		url: productData.value.size_guide,
	};
});

const lowestBasePrice = computed(() => {
	return productData.value.colours.reduce((acc, colour) => {
		const lowestPrice = colour.stock.reduce((acc, size) => {
			const price = size.price;
			return price < acc ? price : acc;
		}, Infinity);
		return lowestPrice < acc ? lowestPrice : acc;
	}, Infinity);
});

cookies.addChangeListener((event) => {
	taxStatus.value = event.value;
	calculateTotal();
});

watch(basket.value.products, () => {
	calculateTotal();
});

onMounted(async () => {
	const data = await fetch(`/wp-json/product-builder/v1/data/${props.id}`).then(
		(res) => res.json()
	);

	if (data) {
		productData.value.pricing = data.quantity_pricing;
		productData.value.colours = data.available_colours;
		productData.value.sizes = data.available_sizes;
		productData.value.personalisation = data.personalisation ?? false;
		productData.value.size_guide = data.size_guide_link;
		productData.value.previous_logo = data.previous_logo ?? [];
		productData.value.comments_only = data.comments_only ?? false;
		productData.value.non_taxable = data.non_taxable ?? false;
		pending.value = false;
		productData.value.ctas_description = data.ctas_description;
		productData.value.phone_cta = data.phone_cta;
		productData.value.phone_number = data.phone_number;
		productData.value.form_cta = data.form_cta;
		productData.value.form_shortcode = data.form_shortcode;
	}
});
</script>

<template>
	<div>
		<template v-if="pending">
			<BuilderSkeltonLoader />
		</template>

		<template v-else>
			<form @submit.prevent class="u-pos-rel">
				<AppSection v-if="productData.pricing">
					<BuilderPricing
						:pricing="productData.pricing"
						:basePrice="lowestBasePrice"
						:ctasDescription="productData.ctas_description"
						:phoneCta="productData.phone_cta"
						:phoneNumber="productData.phone_number"
						:formCta="productData.form_cta"
						:formShortcode="productData.form_shortcode"
					/>
				</AppSection>

				<AppSection
					v-if="productData.colours"
					heading="Step 1: Select your product colours"
				>
					<BuilderColourPicker
						:colours="productData.colours"
						v-model="basket"
					/>
				</AppSection>

				<template
					v-if="basket.products && basket.products.length && productData.sizes"
				>
					<AppSection
						v-for="(colourSelection, index) in basket.products"
						:heading="
							index === 0 ? 'Step 2: Select your sizes and quantities' : ''
						"
						:link="index === 0 ? sizeGuideLink : null"
						:key="index"
					>
						<BuilderSizeSelector
							:sizes="productData.sizes"
							:colourBasket="colourSelection"
							v-model="basket"
						/>
					</AppSection>

					<AppSection
						v-for="(_, index) in basket.personalisation"
						:heading="
							index === 0
								? 'Step 3: Upload your logo, then select position and type'
								: ''
						"
						:key="index"
						v-if="productData.personalisation && !productData.comments_only"
					>
						<BuilderPersonalisation
							v-model="basket.personalisation[index]"
							:personalisationInformation="productData.personalisation"
							:previousLogo="productData.previous_logo"
							:index="index"
							@@removePersonalisationItem="
								handleRemovePersonalisationItem(index)
							"
							@@updatePersonalisationItem="calculateTotal()"
						/>
					</AppSection>

					<AppSection heading="Add Name/s Comments">
						<textarea
							class="u-textarea"
							placeholder="Text here..."
							v-model="basket.comments"
						/>
					</AppSection>

					<AppSection>
						<BuilderAddToBasket
							:personalisation="productData.personalisation ? true : false"
							:commentsOnly="productData.comments_only ?? false"
							:basket="basket"
							:addingToBasket="isAddingToBasket"
							:taxStatus="taxStatusBoolean"
							:nonTaxable="productData.non_taxable"
							@@addToBasket="addToBasket()"
							@@addPersonalisationSection="handleAddPersonalisationItem()"
						/>
					</AppSection>
				</template>

				<div v-if="isAddingToBasket" class="u-overlay">
					<ion-icon :icon="refresh"></ion-icon>
				</div>

				<div v-if="successNotification" class="u-overlay">
					<div class="u-tac">
						<p class="e-heading e-heading--four e-heading--primary u-p">
							{{ successNotification }}
						</p>
						<a href="/cart" class="e-btn e-btn--primary u-mt">Go to basket</a>
					</div>
				</div>
			</form>
		</template>
	</div>
</template>
